/**
 * Image preparation for Tesseract OCR.
 *
 * Two concerns are handled here before the image reaches the OCR worker:
 *
 * 1. EXIF orientation correction
 *    Phone cameras embed an Orientation tag in JPEG EXIF data rather than
 *    rotating the pixel data. Tesseract is sensitive to rotation, so the image
 *    must be in upright orientation before recognition.
 *
 *    Approach: decode via an <img> element, then draw to canvas.
 *    Modern browsers (Chrome 81+, Firefox 26+, Safari 14.1+, all iOS Safari
 *    via WebKit's native EXIF handling) apply EXIF orientation when rendering
 *    <img> elements and carry that correction through to canvas.drawImage().
 *    This is the most cross-platform approach and works correctly on iOS Safari
 *    where createImageBitmap({ imageOrientation }) is only available since iOS 16.
 *
 * 2. Resolution downscaling
 *    Modern phone cameras produce 5–15 MB images at 12–50 MP. Passing
 *    full-resolution input to Tesseract.js exhausts the WASM heap on mid-range
 *    Android devices. Capping the longest side at MAX_OCR_DIMENSION (2000 px)
 *    reduces the image to ~4 MP while preserving enough resolution for OCR.
 */

/** Longest-side cap in pixels. Values above this are downscaled proportionally. */
const MAX_OCR_DIMENSION = 2000

/**
 * The browser could not decode the file as an image. A distinct class so a
 * caller can tell "this file is not a usable image" from any other failure
 * without reading the (English) message.
 */
export class ImageDecodeError extends Error {
  constructor() {
    super('Could not decode image. Try a different file.')
    this.name = 'ImageDecodeError'
  }
}

/**
 * Prepares an image for Tesseract:
 * - File: applies EXIF orientation correction (via <img> decode) + downscales.
 * - HTMLCanvasElement: pixels are already orientation-correct (the browser
 *   applied EXIF when the image was rendered for the crop preview), so only
 *   downscaling is applied.
 *
 * Returns an HTMLCanvasElement that Tesseract.js accepts as OcrImageSource.
 */
export async function prepareImageForOcr(
  input: File | HTMLCanvasElement,
): Promise<HTMLCanvasElement> {
  if (input instanceof HTMLCanvasElement) {
    return downscaleCanvas(input)
  }

  // File path: decode via <img> (applies EXIF orientation), then downscale.
  const url = URL.createObjectURL(input)
  try {
    const img = await decodeImage(url)

    const longest = Math.max(img.naturalWidth, img.naturalHeight)
    const scale = longest > MAX_OCR_DIMENSION ? MAX_OCR_DIMENSION / longest : 1
    const w = Math.round(img.naturalWidth * scale)
    const h = Math.round(img.naturalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not get canvas 2D context')

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(img, 0, 0, w, h)

    return canvas
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Downscales a canvas to fit within MAX_OCR_DIMENSION on its longest side.
 * Returns the same canvas when it is already small enough (no copy made).
 */
function downscaleCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const longest = Math.max(source.width, source.height)
  const scale = longest > MAX_OCR_DIMENSION ? MAX_OCR_DIMENSION / longest : 1
  if (scale === 1) return source

  const w = Math.round(source.width * scale)
  const h = Math.round(source.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get canvas 2D context')

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(source, 0, 0, w, h)
  return canvas
}

function decodeImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new ImageDecodeError())
    img.src = src
  })
}
